<script setup lang="ts">
import { reactive, ref, onMounted } from 'vue';
import type { FormInstance, FormRules } from 'element-plus';
import { message as ElMessage } from '../../utils/message';
import api from '../../api';
import { useAuthStore } from '../../store/authStore';
import AppAlert from '../ui/AppAlert.vue';
import SettingsSidebar from '../settings/SettingsSidebar.vue';
import PhoneInput from '../ui/PhoneInput.vue';

const store       = useAuthStore() as any;
const loading     = ref(true);
const ruleFormRef = ref<FormInstance>();
const formError   = ref('');

const ruleForm = reactive({
  name: '', contact_person: '', email: '', phone_no: '',
});


const rules = reactive<FormRules>({
  name:           [{ required: true, message: 'Company name is required', trigger: 'blur' }],
  contact_person: [{ required: true, message: 'Contact person is required', trigger: 'blur' }],
  email:          [{ required: true, message: 'Email is required', trigger: 'blur' }],
});

// ── Billing contacts ──────────────────────────────────────────────────────────
interface BillingContact { name: string; email: string; }
const billingContacts  = ref<BillingContact[]>([]);
const billingLoading   = ref(false);
const newBillingName   = ref('');
const newBillingEmail  = ref('');
const billingError     = ref('');

const loadBillingContacts = async () => {
  try {
    const res = await api.get('customer/billing-contacts');
    billingContacts.value = res.data.data.contacts || [];
  } catch { /* silent */ }
};

const addBillingContact = async () => {
  billingError.value = '';
  const email = newBillingEmail.value.trim().toLowerCase();
  const name  = newBillingName.value.trim();
  if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    billingError.value = 'Enter a valid email address.'; return;
  }
  if (billingContacts.value.some(c => c.email === email)) {
    billingError.value = 'This email is already in the list.'; return;
  }
  const updated = [...billingContacts.value, { name: name || email, email }];
  await saveBillingContacts(updated);
  newBillingName.value  = '';
  newBillingEmail.value = '';
};

const removeBillingContact = async (email: string) => {
  const updated = billingContacts.value.filter(c => c.email !== email);
  await saveBillingContacts(updated);
};

const saveBillingContacts = async (contacts: BillingContact[]) => {
  billingLoading.value = true;
  try {
    const res = await api.patch('customer/billing-contacts', { contacts });
    billingContacts.value = res.data.data.contacts || contacts;
    ElMessage.success('Billing contacts updated');
  } catch (err: any) {
    billingError.value = err.response?.data?.message || 'Failed to update billing contacts.';
  } finally {
    billingLoading.value = false;
  }
};

const loadBusiness = async () => {
  try {
    const res = await api.get('customer/business');
    const b   = res.data.data.business;
    ruleForm.name           = b.name           || '';
    ruleForm.contact_person = b.contact_person || (store.user as any)?.name || '';
    ruleForm.email          = b.email          || '';
    ruleForm.phone_no       = b.phone          || '';
  } catch {
    ElMessage.error('Failed to load business profile.');
  } finally {
    loading.value = false;
  }
};

const submitForm = async (formEl: FormInstance | undefined) => {
  if (!formEl) return;
  if (store.user?.role !== 'admin') {
    ElMessage.warning('Only admins can edit the business profile.'); return;
  }
  await formEl.validate(async (valid) => {
    if (!valid) return;
    formError.value = '';
    try {
      await api.patch('customer/business', {
        name:           ruleForm.name,
        contact_person: ruleForm.contact_person,
        email:          ruleForm.email,
        phone:          ruleForm.phone_no,
      });
      ElMessage.success('Business profile updated');
    } catch (err: any) {
      formError.value = err.response?.data?.message || 'Failed to update business profile.';
    }
  });
};

onMounted(() => { loadBusiness(); loadBillingContacts(); });
</script>

<template>
  <div class="st-page">

    <div class="st-header">
      <div class="st-inner">
        <h1 class="st-title">Settings</h1>
        <p class="st-subtitle">Manage your account and business preferences</p>
      </div>
      <div class="st-header-rule"></div>
    </div>

    <div class="st-inner st-body">
      <SettingsSidebar />

      <div class="st-content">
        <div class="st-card" v-loading="loading">

          <!-- Hero -->
          <div class="st-hero">
            <div class="st-hero-mark">{{ ruleForm.name?.charAt(0)?.toUpperCase() || 'B' }}</div>
            <div class="st-hero-info">
              <div class="st-hero-name">{{ ruleForm.name || 'Your Company' }}</div>
              <div class="st-hero-email">{{ ruleForm.email || 'company@email.com' }}</div>
              <div class="st-hero-badges">
                <span class="st-hero-badge">Business Account</span>
                <span class="st-hero-badge st-hero-badge--admin" v-if="store.user?.role === 'admin'">Admin</span>
              </div>
            </div>
            <div class="st-hero-contact" v-if="ruleForm.contact_person">
              <div class="st-hero-contact-label">Primary contact</div>
              <div class="st-hero-contact-name">{{ ruleForm.contact_person }}</div>
            </div>
          </div>

          <!-- Two-column body -->
          <div class="st-form-body">
            <div class="st-two-col">

              <!-- Left: company + contact form -->
              <div class="st-col-main">
                <div class="st-section-label">Company Information</div>
                <AppAlert :message="formError" />

                <el-form ref="ruleFormRef" :model="ruleForm" :rules="rules" label-width="0px" status-icon>
                  <div class="st-grid">
                    <div class="st-field">
                      <label class="st-label">Company Name <span class="st-req">*</span></label>
                      <el-form-item prop="name" class="st-fi">
                        <input v-model="ruleForm.name" class="st-input" placeholder="Your company name" />
                      </el-form-item>
                    </div>
                    <div class="st-field">
                      <label class="st-label">Business Email <span class="st-req">*</span></label>
                      <el-form-item prop="email" class="st-fi">
                        <input v-model="ruleForm.email" class="st-input" type="email" placeholder="company@email.com" />
                      </el-form-item>
                    </div>
                  </div>

                  <div class="st-section-label" style="margin-top: 4px;">Contact Details</div>

                  <div class="st-grid">
                    <div class="st-field">
                      <label class="st-label">Contact Person <span class="st-req">*</span></label>
                      <el-form-item prop="contact_person" class="st-fi">
                        <input v-model="ruleForm.contact_person" class="st-input" placeholder="Primary contact name" />
                      </el-form-item>
                    </div>
                    <div class="st-field">
                      <label class="st-label">Phone Number</label>
                      <PhoneInput v-model="ruleForm.phone_no" />
                    </div>
                  </div>

                  <div class="st-actions">
                    <span class="st-admin-note" v-if="store.user?.role !== 'admin'">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                      Only admins can edit this
                    </span>
                    <button class="st-btn" @click.prevent="submitForm(ruleFormRef)" :disabled="loading">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                      Save Changes
                    </button>
                  </div>
                </el-form>
              </div>

              <!-- Divider -->
              <div class="st-col-divider"></div>

              <!-- Right: billing emails -->
              <div class="st-col-side">
                <div class="st-section-label">Billing Emails</div>
                <p class="st-hint">Invoices will be sent to these addresses. If none are set, we use the business email above.</p>

                <div class="billing-list" v-if="billingContacts.length">
                  <div class="billing-row" v-for="contact in billingContacts" :key="contact.email">
                    <div class="billing-av">{{ (contact.name || contact.email).charAt(0).toUpperCase() }}</div>
                    <div class="billing-info">
                      <div class="billing-name">{{ contact.name }}</div>
                      <div class="billing-email">{{ contact.email }}</div>
                    </div>
                    <button
                      v-if="store.user?.role === 'admin'"
                      class="billing-remove"
                      @click="removeBillingContact(contact.email)"
                      :disabled="billingLoading"
                      title="Remove"
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </button>
                  </div>
                </div>
                <div class="billing-empty" v-else>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,12 2,6"/></svg>
                  No billing contacts added yet
                </div>

                <div class="billing-add" v-if="store.user?.role === 'admin'">
                  <div class="billing-add-col">
                    <input v-model="newBillingName"  class="st-input" placeholder="Name (optional)" />
                    <div style="display:flex; gap:8px; align-items:center;">
                      <input v-model="newBillingEmail" class="st-input" style="flex:1;" placeholder="Email address *" type="email" @keydown.enter.prevent="addBillingContact" />
                      <button class="billing-add-btn" @click="addBillingContact" :disabled="billingLoading">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                        Add
                      </button>
                    </div>
                  </div>
                  <p class="billing-error" v-if="billingError">{{ billingError }}</p>
                </div>
              </div>

            </div>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&display=swap');
* { box-sizing: border-box; }

.st-page { font-family: 'DM Sans', sans-serif; background: white; min-height: 100%; }
.st-inner { max-width: 1280px; margin: 0 auto; padding: 0 32px; }

.st-header { background: white; padding: 24px 0 0; }
.st-header .st-inner { padding-bottom: 18px; }
.st-title { font-size: 1.4rem; font-weight: 700; color: #0f1729; margin: 0 0 3px; }
.st-subtitle { font-size: 0.83rem; color: #94a3b8; margin: 0; }
.st-header-rule { height: 1.5px; background: #e8f0ff; }

.st-body { display: flex; gap: 24px; padding-top: 28px; padding-bottom: 40px; align-items: flex-start; }
.st-content { flex: 1; min-width: 0; }

.st-card { background: white; border-radius: 14px; border: 1.5px solid #ccdeff; overflow: hidden; }

/* Hero */
.st-hero {
  background: linear-gradient(130deg, #0061eb 0%, #003a96 100%);
  padding: 28px 32px; display: flex; align-items: center; gap: 20px;
  position: relative; overflow: hidden;
}
.st-hero::before {
  content: ''; position: absolute; inset: 0;
  background: radial-gradient(circle at 80% 50%, rgba(255,255,255,0.07) 0%, transparent 60%);
}
.st-hero-mark {
  width: 64px; height: 64px; border-radius: 16px; flex-shrink: 0;
  background: rgba(255,255,255,0.18); border: 2px solid rgba(255,255,255,0.3);
  color: white; display: flex; align-items: center; justify-content: center;
  font-size: 1.8rem; font-weight: 700; position: relative; z-index: 1;
}
.st-hero-info { flex: 1; position: relative; z-index: 1; }
.st-hero-name { font-size: 1.1rem; font-weight: 700; color: white; margin-bottom: 2px; }
.st-hero-email { font-size: 0.8rem; color: rgba(255,255,255,0.65); margin-bottom: 10px; }
.st-hero-badges { display: flex; gap: 6px; }
.st-hero-badge {
  display: inline-block; background: rgba(255,255,255,0.15); border: 1px solid rgba(255,255,255,0.25);
  color: rgba(255,255,255,0.9); font-size: 0.65rem; font-weight: 700;
  letter-spacing: 0.07em; text-transform: uppercase; border-radius: 20px; padding: 3px 10px;
}
.st-hero-badge--admin { background: rgba(235,31,40,0.35); border-color: rgba(235,31,40,0.5); }

.st-hero-contact { position: relative; z-index: 1; text-align: right; flex-shrink: 0; }
.st-hero-contact-label { font-size: 0.65rem; color: rgba(255,255,255,0.5); text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 3px; }
.st-hero-contact-name { font-size: 0.9rem; font-weight: 600; color: white; }

/* Form */
.st-form-body { padding: 28px 32px; }
.st-two-col { display: grid; grid-template-columns: 1fr auto 340px; gap: 0; align-items: start; }
.st-col-main { padding-right: 32px; }
.st-col-divider { width: 1.5px; background: #e8f0ff; align-self: stretch; margin: 0 4px; }
.st-col-side { padding-left: 32px; }
.st-section-label {
  font-size: 0.67rem; font-weight: 700; color: #0061eb;
  text-transform: uppercase; letter-spacing: 0.08em;
  margin-bottom: 16px; padding-bottom: 8px; border-bottom: 1.5px solid #e8f0ff;
}
.st-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 16px; }
.st-field { display: flex; flex-direction: column; }
.st-label { font-size: 0.78rem; font-weight: 600; color: #475569; margin-bottom: 6px; }
.st-req { color: #dc2626; }
.st-fi { margin-bottom: 0 !important; }
:deep(.st-fi .el-form-item__content) { display: block; width: 100%; }
:deep(.st-fi .el-form-item__error) { padding-top: 4px; font-size: 0.74rem; }
.st-input {
  width: 100%; height: 42px; padding: 0 14px;
  border: 1.5px solid #e2e8f0; border-radius: 9px;
  font-family: 'DM Sans', sans-serif; font-size: 0.87rem; color: #1e293b;
  outline: none; transition: border-color 0.15s, box-shadow 0.15s; background: white;
}
.st-input:focus { border-color: #0061eb; box-shadow: 0 0 0 3px rgba(0,97,235,0.08); }
.st-input::placeholder { color: #94a3b8; }

.st-actions {
  display: flex; align-items: center; justify-content: flex-end; gap: 12px;
  margin-top: 24px; padding-top: 18px; border-top: 1px solid #f1f5f9;
}
.st-admin-note {
  display: flex; align-items: center; gap: 5px;
  font-size: 0.78rem; color: #94a3b8;
}
.st-btn {
  height: 42px; padding: 0 28px;
  background: #0061eb; color: white; border: none; border-radius: 9px;
  font-family: 'DM Sans', sans-serif; font-size: 0.88rem; font-weight: 600;
  cursor: pointer; display: flex; align-items: center; gap: 8px; transition: background 0.15s;
}
.st-btn:hover:not(:disabled) { background: #0050c7; }
.st-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.st-hint { font-size: 0.78rem; color: #94a3b8; margin: -10px 0 16px; }

/* Billing emails */
.billing-list { display: flex; flex-direction: column; gap: 8px; margin-bottom: 16px; }
.billing-row {
  display: flex; align-items: center; gap: 12px;
  padding: 10px 14px; border-radius: 10px;
  border: 1.5px solid #ccdeff; background: #f8fafc;
}
.billing-av {
  width: 34px; height: 34px; border-radius: 8px; flex-shrink: 0;
  background: #0061eb; color: white;
  display: flex; align-items: center; justify-content: center;
  font-size: 0.85rem; font-weight: 700;
}
.billing-info { flex: 1; min-width: 0; }
.billing-name { font-size: 0.85rem; font-weight: 600; color: #111827; }
.billing-email { font-size: 0.75rem; color: #64748b; margin-top: 1px; }
.billing-remove {
  background: none; border: none; cursor: pointer; padding: 4px;
  color: #cbd5e1; border-radius: 6px; transition: color 0.15s, background 0.15s;
  display: flex; align-items: center; flex-shrink: 0;
}
.billing-remove:hover:not(:disabled) { color: #ef4444; background: #fee2e2; }
.billing-remove:disabled { opacity: 0.4; cursor: not-allowed; }
.billing-empty {
  display: flex; align-items: center; gap: 8px;
  font-size: 0.82rem; color: #94a3b8;
  padding: 14px 0; margin-bottom: 16px;
}
.billing-add { margin-top: 4px; }
.billing-add-col { display: flex; flex-direction: column; gap: 8px; }
.billing-add-btn {
  height: 42px; padding: 0 18px; flex-shrink: 0;
  background: #0061eb; color: white; border: none; border-radius: 9px;
  font-family: 'DM Sans', sans-serif; font-size: 0.85rem; font-weight: 600;
  cursor: pointer; display: flex; align-items: center; gap: 6px; transition: background 0.15s;
}
.billing-add-btn:hover:not(:disabled) { background: #0050c7; }
.billing-add-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.billing-error { font-size: 0.75rem; color: #dc2626; margin-top: 6px; }
</style>
